import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { StatsOverview } from "@/components/statistics/StatsOverview";
import { PopularRanges } from "@/components/statistics/PopularRanges";
import { NumberFrequency } from "@/components/statistics/NumberFrequency";
import { GenerationTimeline } from "@/components/statistics/GenerationTimeline";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import statisticsImage from "@/assets/statistics-hero.jpg";

const Statistics = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalGenerations: 0,
    totalNumbers: 0,
    uniqueUsers: 0,
    avgPerGeneration: 0
  });
  const [popularRanges, setPopularRanges] = useState<Array<{ range: string; count: number; percentage: number }>>([]);
  const [numberFrequencies, setNumberFrequencies] = useState<Record<number, number>>({});
  const [timeline, setTimeline] = useState<Array<{ date: string; count: number }>>([]);

  useEffect(() => {
    fetchStatistics();

    // Auto-increment stats every 20 seconds
    const interval = setInterval(() => {
      setStats(prev => {
        const increment = Math.floor(Math.random() * 6) + 25; // Random 25-30
        const newTotalNumbers = prev.totalNumbers + increment;
        const newTotalGenerations = prev.totalGenerations + increment;
        
        // Save increments to localStorage
        const currentIncrement = parseInt(localStorage.getItem('stats_increment') || '0');
        localStorage.setItem('stats_increment', (currentIncrement + increment).toString());
        
        return {
          totalNumbers: newTotalNumbers,
          totalGenerations: newTotalGenerations,
          uniqueUsers: prev.uniqueUsers,
          avgPerGeneration: newTotalNumbers / newTotalGenerations
        };
      });
    }, 20000); // Every 20 seconds

    return () => clearInterval(interval);
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);

      // Fetch all generations (no RLS on SELECT for statistics)
      const { data: generations, error } = await supabase
        .from('number_generations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!generations || generations.length === 0) {
        setLoading(false);
        return;
      }

      // Calculate overview stats
      const totalGenerations = generations.length;
      const totalNumbers = generations.reduce((sum, gen) => sum + gen.count, 0);
      const uniqueIPs = new Set(generations.map(g => g.ip_address).filter(Boolean)).size;
      
      // Apply accumulated increments from localStorage
      const savedIncrement = parseInt(localStorage.getItem('stats_increment') || '0');
      const adjustedTotalNumbers = totalNumbers + savedIncrement;
      const adjustedTotalGenerations = totalGenerations + savedIncrement;
      const avgPerGeneration = adjustedTotalNumbers / adjustedTotalGenerations;

      setStats({
        totalGenerations: adjustedTotalGenerations,
        totalNumbers: adjustedTotalNumbers,
        uniqueUsers: uniqueIPs,
        avgPerGeneration
      });

      // Calculate popular ranges
      const rangeMap = new Map<string, number>();
      generations.forEach(gen => {
        const range = `${gen.min_value}-${gen.max_value}`;
        rangeMap.set(range, (rangeMap.get(range) || 0) + 1);
      });

      const ranges = Array.from(rangeMap.entries())
        .map(([range, count]) => ({
          range,
          count,
          percentage: (count / totalGenerations) * 100
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      setPopularRanges(ranges);

      // Calculate number frequencies
      const freqMap: Record<number, number> = {};
      generations.forEach(gen => {
        gen.generated_numbers.forEach((num: number) => {
          freqMap[num] = (freqMap[num] || 0) + 1;
        });
      });

      setNumberFrequencies(freqMap);

      // Calculate timeline (last 30 days)
      const timelineMap = new Map<string, number>();
      const last30Days = new Date();
      last30Days.setDate(last30Days.getDate() - 30);

      generations
        .filter(gen => new Date(gen.created_at) >= last30Days)
        .forEach(gen => {
          const date = new Date(gen.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          timelineMap.set(date, (timelineMap.get(date) || 0) + 1);
        });

      const timelineData = Array.from(timelineMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      setTimeline(timelineData);

    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SEO 
        title="Statistics & Analytics - Random Number Generation Data"
        description="View real-time statistics and analytics of random number generation patterns. Track popular ranges, number frequency, and generation trends."
        canonical="https://numbers-generator.com/statistics"
        keywords="statistics, analytics, random number data, number frequency, generation patterns"
      />
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/5 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            RandomGen Statistics & Analytics
          </h1>
          <p className="text-center text-muted-foreground mb-12">
            Real-time insights into random number generation patterns
          </p>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-8">
              <StatsOverview {...stats} />
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <PopularRanges ranges={popularRanges} />
                <GenerationTimeline data={timeline} />
              </div>

              <NumberFrequency frequencies={numberFrequencies} />
              
              {/* Statistics Hero Image at Bottom */}
              <div className="rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto mt-12">
                <img 
                  src={statisticsImage} 
                  alt="Analytics dashboard displaying colorful charts, graphs, and data visualization - real-time statistics and insights for random number generation patterns"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Statistics;
